import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type CryptoAsset = 'USDT' | 'USDC';
export type CryptoNetwork =
  | 'tron'
  | 'ethereum'
  | 'bsc'
  | 'polygon'
  | 'arbitrum'
  | 'optimism'
  | 'base'
  | 'avalanche'
  | 'solana'
  | 'ton';

export type CryptoWallet = {
  id: string;
  asset: CryptoAsset;
  network: CryptoNetwork;
  address: string;
  memo: string | null;
  label: string | null;
  min_confirmations: number;
  enabled: boolean;
  sort_order: number;
  created_at: number;
  updated_at: number;
};

export type NetworkCatalog = {
  id: CryptoNetwork;
  label: string;
  short_label: string;
  memo_supported: boolean;
  notes?: string;
};

export type WalletCatalog = {
  assets: Array<{ id: CryptoAsset; label: string; decimals: number }>;
  networks: NetworkCatalog[];
  supported_combos: Array<{ asset: CryptoAsset; networks: CryptoNetwork[] }>;
};

// ---------- Platform CRUD ----------

export function usePlatformWallets() {
  return useQuery({
    queryKey: ['platform', 'crypto-wallets'],
    queryFn: () =>
      api.get<{ wallets: CryptoWallet[]; catalog: WalletCatalog }>(
        '/api/platform/crypto-wallets',
      ),
  });
}

export function useCreateWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      asset: CryptoAsset;
      network: CryptoNetwork;
      address: string;
      memo?: string;
      label?: string;
      min_confirmations?: number;
      enabled?: boolean;
    }) => api.post<{ wallet: CryptoWallet }>('/api/platform/crypto-wallets', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'crypto-wallets'] }),
  });
}

export function useUpdateWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: {
        label?: string;
        memo?: string;
        min_confirmations?: number;
        enabled?: boolean;
        sort_order?: number;
      };
    }) => api.patch<{ wallet: CryptoWallet }>(`/api/platform/crypto-wallets/${args.id}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'crypto-wallets'] }),
  });
}

export function useDeleteWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/platform/crypto-wallets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'crypto-wallets'] }),
  });
}

// ---------- User: wallet list for cart checkout ----------

export type PublicWallet = {
  id: string;
  asset: CryptoAsset;
  network: CryptoNetwork;
  address: string;
  memo: string | null;
  label: string | null;
  min_confirmations: number;
};

export function useActiveWallets() {
  return useQuery({
    queryKey: ['billing', 'crypto-wallets'],
    queryFn: () => api.get<{ wallets: PublicWallet[] }>('/api/app/billing/crypto-wallets'),
  });
}

// -------- Carts (multi-item crypto) --------

export type AdminCart = {
  id: string;
  organization_id: string;
  user_id: string;
  payment_method: string;
  status: string;
  total_amount_usd_cents: number;
  crypto_asset: string | null;
  crypto_network: CryptoNetwork | null;
  crypto_address: string | null;
  crypto_expected_amount: string | null;
  crypto_txid: string | null;
  crypto_txid_submitted_at: number | null;
  verification_notes: string | null;
  paid_at: number | null;
  created_at: number;
  user_email: string | null;
  org_name: string | null;
  item_count: number;
  verify_attempts?: number;
  verify_last_attempt?: number | null;
  verify_auto?: number;
};

export type AdminCartItem = {
  id: string;
  package_id: string;
  package_name: string | null;
  amount_paid: number | null;
  status: string;
  kind: string | null;
  duration_days: number | null;
};

export function useAdminCarts() {
  return useQuery({
    queryKey: ['platform', 'carts'],
    queryFn: () => api.get<{ carts: AdminCart[] }>('/api/platform/carts'),
    refetchInterval: 30_000,
  });
}

export function useAdminCart(id: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'carts', id],
    queryFn: () => api.get<{ cart: AdminCart; items: AdminCartItem[] }>(`/api/platform/carts/${id}`),
    enabled: !!id,
  });
}

export function useApproveCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; note?: string }) =>
      api.post<{ approved: boolean }>(`/api/platform/carts/${args.id}/approve-crypto`, {
        note: args.note,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'carts'] }),
  });
}

export function useRejectCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<{ rejected: boolean }>(`/api/platform/carts/${args.id}/reject-crypto`, {
        reason: args.reason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'carts'] }),
  });
}

export function useCancelCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; note?: string }) =>
      api.post<{ cancelled: boolean }>(`/api/platform/carts/${args.id}/cancel`, {
        note: args.note,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'carts'] });
    },
  });
}

export function useUncancelCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ uncancelled: boolean }>(`/api/platform/carts/${id}/uncancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'carts'] });
    },
  });
}

export function useDeleteCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/platform/carts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'carts'] });
    },
  });
}
